import {Collapse, Grid, IconButton, Stack, Typography} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {DataUploadTextInput} from "./TextInput";
import {Farm} from "../../../types/domain/farm";
import {FieldInsightItem} from "../../../types/fieldInsightItem";
import {useCallback, useEffect, useState} from "react";
import {Item} from "../../../types/uploadDataEnums";
import {LastAppliedIrrigation} from "../../../types/domain/appliedIrrigation";

export interface Props {
    farm?: Farm;
    fields?: FieldInsightItem[];
    setFields: (fields: Item[]) => void;
    initValues: LastAppliedIrrigation[];
    clear: boolean;
    setClear: (value: boolean) => void;
}

export const DataUploadIrrigationRainfallItem: React.FC<Props> = ({
    farm,
    fields,
    setFields,
    initValues,
    clear,
    setClear,
}) => {
    const [open, setOpen] = useState(false);
    const [farmValue, setFarmValue] = useState<Item>(DEFAULT_VALUES);
    const [fieldsValue, setFieldsValue] = useState<Item[]>(
        (fields ?? []).map((field) => ({...DEFAULT_VALUES, id: field.field.id})),
    );

    const isBothBlank = (item: Item): boolean => {
        return item.irrigation.value === null && item.rainfall.value === null;
    };

    const checkFarmError = useCallback(() => {
        const subFieldsHaveError = fieldsValue.some((item) => item.error);
        const farmBothBlank = farmValue.irrigation.value === null && farmValue.rainfall.value === null;

        if ((subFieldsHaveError || farmBothBlank) && !farmValue.error) {
            setFarmValue({...farmValue, error: true});
        } else if (!subFieldsHaveError && !farmBothBlank && farmValue.error) {
            setFarmValue({...farmValue, error: false});
        }
    }, [fieldsValue, farmValue]);

    const handleSetFieldsValue = useCallback(
        (id: string, newValue: Item) => {
            const newArray = fieldsValue.map((value) => (value.id === id ? newValue : value));
            setFieldsValue(newArray);
        },
        [fieldsValue],
    );

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        fieldsValue.map((value) => {
            const item = value;
            if (!value.irrigation.changed && farmValue.irrigation.value !== null) {
                item.irrigation = farmValue.irrigation;
            }
            if (!value.rainfall.changed && farmValue.rainfall.value !== null) {
                item.rainfall = farmValue.rainfall;
            }
            handleSetFieldsValue(value.id!, item);
        });
        checkFarmError();
    }, [farmValue]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        fieldsValue.forEach((value) => {
            const shouldError = isBothBlank(value);
            if (shouldError && !value.error) {
                handleSetFieldsValue(value.id!, {...value, error: true});
            } else if (!shouldError && value.error) {
                handleSetFieldsValue(value.id!, {...value, error: false});
            }
        });

        checkFarmError();
        setFields(fieldsValue);
    }, [fieldsValue]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setFarmValue({
            ...farmValue,
            irrigation: {value: null, changed: false},
            rainfall: {value: null, changed: false},
            error: false,
        });

        setFieldsValue(
            fieldsValue.map((item) => {
                const value = initValues.find((value) => value.fieldId === item.id);
                return {
                    ...item,
                    irrigation: {...item.irrigation, value: value ? value.irrigation : null},
                    rainfall: {...item.rainfall, value: value ? value.rainfall : null},
                };
            }),
        );
    }, [initValues]);

    useEffect(() => {
        if (clear) {
            setFieldsValue((prevFields) =>
                prevFields.map((item) => ({
                    ...item,
                    irrigation: {value: null, changed: false},
                    rainfall: {value: null, changed: false},
                    error: false,
                })),
            );

            setFarmValue((prev) => ({
                ...prev,
                irrigation: {value: null, changed: false},
                rainfall: {value: null, changed: false},
                error: false,
            }));
            setClear(false);
        }
    }, [clear, setClear]);

    return (
        <Stack>
            <Stack
                direction="row"
                sx={{padding: "3px 10px", backgroundColor: "#F3F5FA", borderTop: "1px solid #E9EBF1"}}
            >
                <Grid item xs={7}>
                    <Stack direction="row" sx={{mt: "0.25rem"}}>
                        <IconButton
                            sx={{mr: "4px", padding: 0}}
                            onClick={(e) => {
                                if (fields && fields.length > 0) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpen(!open);
                                }
                            }}
                        >
                            {open ? (
                                <ExpandMoreIcon fontSize="small" color="primary" />
                            ) : (
                                <ChevronRightIcon fontSize="small" color="primary" />
                            )}
                        </IconButton>
                        <Typography fontSize={14} fontWeight={500} textTransform="uppercase" color="#656B80">
                            {farm?.name}
                        </Typography>
                    </Stack>
                </Grid>
                <Grid item xs={2}>
                    <DataUploadTextInput
                        clear={clear}
                        value={farmValue.irrigation.value}
                        setValue={(value) => setFarmValue({...farmValue, irrigation: {value, changed: false}})}
                    />
                </Grid>
                <Grid item xs={2}>
                    <DataUploadTextInput
                        clear={clear}
                        value={farmValue.rainfall.value}
                        setValue={(value) => setFarmValue({...farmValue, rainfall: {value, changed: false}})}
                    />
                </Grid>
                <Grid item xs={1} textAlign="right" sx={{mt: "0.25rem"}}>
                    {farmValue.error && <CloseIcon htmlColor="red" fontSize="small" />}
                    {!farmValue.error && <CheckIcon htmlColor="#0AA043" fontSize="small" />}
                </Grid>
            </Stack>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {fields?.map((field, i) => (
                    <Stack key={i} direction="row" sx={{padding: "3px 10px", borderTop: "1px solid #E9EBF1"}}>
                        <Grid item xs={7}>
                            <Stack direction="row" sx={{mt: "0.25rem", ml: "1.5rem"}}>
                                <Typography fontSize={14} fontWeight={500} color="#2B303F">
                                    {field?.name}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={2}>
                            <DataUploadTextInput
                                clear={clear}
                                value={fieldsValue[i].irrigation.value}
                                setValue={(value) =>
                                    handleSetFieldsValue(fieldsValue[i].id!, {
                                        ...fieldsValue[i],
                                        irrigation: {value, changed: true},
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <DataUploadTextInput
                                clear={clear}
                                value={fieldsValue[i].rainfall.value}
                                setValue={(value) =>
                                    handleSetFieldsValue(fieldsValue[i].id!, {
                                        ...fieldsValue[i],
                                        rainfall: {value, changed: true},
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={1} textAlign="right" sx={{mt: "0.25rem"}}>
                            {fieldsValue[i].error && <CloseIcon htmlColor="red" fontSize="small" />}
                            {!fieldsValue[i].error && <CheckIcon htmlColor="#0AA043" fontSize="small" />}
                        </Grid>
                    </Stack>
                ))}
            </Collapse>
        </Stack>
    );
};

const DEFAULT_VALUES: Item = {
    id: null,
    rainfall: {
        value: null,
        changed: false,
    },
    irrigation: {
        value: null,
        changed: false,
    },
    date: undefined,
    error: false,
};
